import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";

export interface Profile {
  nuid: string;
  primaryRole: string;
  primaryRegion: string;
  regionDescription: string;
  currentRegion: string;
  isAdmin: boolean;
  isLmp: boolean;
  viewDataPrivileges: Privilege[];
  editDataPrivileges: Privilege[];
  adminDataPrivileges: Privilege[];
  teamMemberships: TeamMembership[];
}

/** A location within a service area center. */
export interface PrivilegeLocation {
  regionCode: string;
  areaCode: string;
  centerCode: string;
  locationCode: string;
  locationName: string;
}

/** A service area center containing optional locations. */
export interface PrivilegeCenter {
  center: {
    regionCode: string;
    areaCode: string;
    centerCode: string;
    centerDescription: string;
  };
  locations: PrivilegeLocation[] | null;
}

/** An area containing optional centers. */
export interface PrivilegeArea {
  area: {
    regionCode: string;
    areaCode: string;
    areaDescription: string;
  };
  centers: PrivilegeCenter[] | null;
}

/** A region-level privilege entry with optional nested area hierarchy. */
export interface Privilege {
  region: {
    regionCode: string;
    regionDescription: string;
  };
  areas: PrivilegeArea[] | null;
}

interface TeamMembership {
  teamId: number;
  nuid: string;
  teamRole: string;
  regionCode: string;
  regionDescription: string;
  areaCode: string;
  areaDescription: string;
  centerCode: string;
  centerDescription: string;
  locationCode: string | null;
  locationName: string | null;
}

export interface ProfileState {
  profile: Profile;
  loading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  profile: {} as Profile,
  loading: false,
  error: null,
};

export const fetchProfile = createAsyncThunk(
  "profile/fetchProfile",
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal };
      const url = endpoints.ubt.profile.getProfile;
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled"
      ) {
        return rejectWithValue("Request cancelled");
      }
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Failed to fetch profile";
      return rejectWithValue(message);
    }
  }
);

const slice = createSlice({
  name: "profile",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchProfile.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchProfile.fulfilled, (state, action) => {
      state.loading = false;
      state.profile = action.payload;
    });
    builder.addCase(fetchProfile.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export default slice.reducer;
